import type { Message, Part, SSEEvent, TextPart, ThinkingPart } from "./types";

// Claude Code injects bookkeeping turns into the user role: the local-command
// caveat, slash-command invocations, and the stdout of local commands. They are
// wire markup, not prose, so the thread renders them as system chrome (or not
// at all) rather than as "You" bubbles.
const META_TAG_PATTERN =
  /<(?:local-command-caveat|local-command-stdout|local-command-stderr|command-name|command-message|command-args)>/;

const CAVEAT_TEXT_PATTERN =
  /^\s*Caveat: The messages below were generated by the user while running local commands/;

// CSI sequences (colors, cursor moves) plus OSC strings; Claude Code's local
// command output carries them and they must never reach the DOM as text.
const ANSI_PATTERN =
  /\u001b\[[0-9;?]*[\u0020-\u002f]*[@-~]|\u001b\][\s\S]*?(?:\u0007|\u001b\\)|\u001b[@-Z\\-_]/g;

export function stripAnsi(text: string): string {
  return text.replace(ANSI_PATTERN, "");
}

export type MetaMessage =
  /** Hide entirely — pure instruction boilerplate for the model. */
  | { kind: "caveat" }
  /** A slash command the user ran, e.g. `/model opus`. */
  | { kind: "command"; name: string; args?: string }
  /** Output produced locally by a slash command. */
  | { kind: "output"; text: string; isError: boolean };

function tagContent(text: string, tag: string): string | null {
  const match = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`).exec(text);
  return match ? match[1] : null;
}

function stripTags(text: string): string {
  return text.replace(/<\/?[a-z-]+>/g, "");
}

/**
 * Classify a Claude Code bookkeeping turn, or null when the message is a real
 * user message that should render normally.
 */
export function classifyMetaMessage(msg: Message): MetaMessage | null {
  if (msg.role !== "user") return null;
  if (msg.parts.some((p) => p.type !== "text")) return null;
  const text = msg.parts
    .map((p) => (p.type === "text" ? p.text : ""))
    .join("\n");
  if (!text) return null;
  if (!META_TAG_PATTERN.test(text) && !CAVEAT_TEXT_PATTERN.test(text)) {
    return null;
  }

  if (text.includes("<local-command-caveat>") || CAVEAT_TEXT_PATTERN.test(text)) {
    return { kind: "caveat" };
  }

  // An empty tag carries no command to show, so fall through to the output
  // branch rather than rendering a blank chip.
  const name = stripAnsi(tagContent(text, "command-name") ?? "").trim();
  if (name) {
    const args = tagContent(text, "command-args");
    return {
      kind: "command",
      name,
      args: args ? stripAnsi(args).trim() || undefined : undefined,
    };
  }

  const stderr = tagContent(text, "local-command-stderr");
  const stdout = stderr ?? tagContent(text, "local-command-stdout");
  const body = stdout ?? stripTags(text);
  return {
    kind: "output",
    text: stripAnsi(body).trim(),
    isError: stderr !== null,
  };
}

export function findPart(
  messages: Message[],
  partId: string,
): { msg: Message; part: Part } | null {
  for (const msg of messages) {
    for (const part of msg.parts) {
      if (part.id === partId) return { msg, part };
    }
  }
  return null;
}

export function applyDelta(
  messages: Message[],
  e: Extract<SSEEvent, { type: "message.part.delta" }>,
): Message[] {
  return messages.map((msg) => {
    if (msg.id !== e.messageId) return msg;
    const parts = msg.parts.map((part) => {
      if (part.id !== e.partId) return part;
      if (part.type === "text") {
        const next: TextPart = { ...part, text: (part.text ?? "") + e.delta };
        return next;
      }
      if (part.type === "thinking") {
        const next: ThinkingPart = {
          ...part,
          text: (part.text ?? "") + e.delta,
        };
        return next;
      }
      return part;
    });
    return { ...msg, parts };
  });
}

export function applyPartUpdate(
  messages: Message[],
  e: Extract<SSEEvent, { type: "message.part.updated" }>,
): Message[] {
  let touched = false;
  const next = messages.map((msg) => {
    if (msg.id !== e.messageId) return msg;
    let found = false;
    const parts = msg.parts.map((part) => {
      if (part.id !== e.part.id) return part;
      found = true;
      return e.part;
    });
    if (!found) {
      parts.push(e.part);
    }
    touched = true;
    return { ...msg, parts };
  });
  if (!touched) return messages;
  return next;
}

export function applyMessageCreated(
  messages: Message[],
  e: Extract<SSEEvent, { type: "message.created" }>,
): Message[] {
  if (messages.some((m) => m.id === e.message.id)) {
    return messages.map((m) => (m.id === e.message.id ? e.message : m));
  }
  return [...messages, e.message];
}
